import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUserFriends, FaChartBar, FaTimes, FaCheck, FaTimes as FaReject } from 'react-icons/fa';
import { ReferralStatisticsChart } from './Placeholder';

const DashboardCard = ({ title, icon: Icon, children, className = '' }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className={`bg-white p-6 rounded-lg shadow-md ${className}`}
  >
    <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-800">
      <Icon className="mr-2 text-blue-500" />
      {title}
    </h2>
    {children}
  </motion.div>
);

const dummyReferrals = [
  { 
    id: 1, 
    name: 'John Smith', 
    position: 'Product Manager', 
    company: 'InnoTech', 
    status: 'Pending',
    bio: 'Experienced product manager with a passion for user-centric design.',
    skills: ['Product Strategy', 'Agile Methodologies', 'User Research'],
    experience: '8 years in tech industry',
    education: 'MBA from Stanford University'
  },
  { 
    id: 2, 
    name: 'Sarah Johnson', 
    position: 'UX Designer', 
    company: 'DesignHub', 
    status: 'Pending',
    bio: 'Creative UX designer focused on creating intuitive and beautiful interfaces.',
    skills: ['UI/UX Design', 'Figma', 'User Testing'],
    experience: '5 years in design agencies',
    education: 'BFA in Graphic Design from RISD'
  },
  { 
    id: 3, 
    name: 'Michael Lee', 
    position: 'Data Analyst', 
    company: 'DataCorp', 
    status: 'Pending',
    bio: 'Data analyst with a knack for finding insights in complex datasets.',
    skills: ['SQL', 'Python', 'Data Visualization'],
    experience: '3 years in data analysis',
    education: 'BS in Statistics from MIT'
  },
];

const UserProfileModal = ({ user, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    onClick={onClose}
  >
    <motion.div
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -50, opacity: 0 }}
      className="bg-white rounded-lg p-8 max-w-md w-full m-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">{user.name}</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <FaTimes size={24} />
        </button>
      </div>
      <p className="text-gray-600 mb-2">{user.position} at {user.company}</p>
      <p className="text-gray-700 mb-4">{user.bio}</p>
      <h3 className="font-semibold mb-2">Skills:</h3>
      <div className="flex flex-wrap gap-2 mb-4">
        {user.skills.map((skill, index) => (
          <span key={index} className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
            {skill}
          </span>
        ))}
      </div>
      <p className="text-gray-700 mb-2"><strong>Experience:</strong> {user.experience}</p>
      <p className="text-gray-700"><strong>Education:</strong> {user.education}</p>
    </motion.div>
  </motion.div>
);

const ReferralCard = ({ referral, onAccept, onReject, onViewProfile }) => (
  <motion.li
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="border-b pb-4 last:border-b-0"
  >
    <div className="flex justify-between items-start">
      <div>
        <h3 className="font-semibold text-lg text-gray-800 cursor-pointer" onClick={() => onViewProfile(referral)}>
          {referral.name}
        </h3>
        <p className="text-gray-600">{referral.position} at {referral.company}</p>
        <p className={`text-sm ${referral.status === 'Approved' ? 'text-green-600' : 'text-yellow-600'}`}>
          Status: {referral.status}
        </p>
      </div>
      <div className="flex space-x-2">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onAccept(referral.id)}
          className="bg-green-500 text-white p-2 rounded-full"
        >
          <FaCheck />
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onReject(referral.id)}
          className="bg-red-500 text-white p-2 rounded-full"
        >
          <FaReject />
        </motion.button>
      </div>
    </div>
  </motion.li>
);

const ReferralsTab = () => {
  const [referrals, setReferrals] = useState(dummyReferrals);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleAccept = (id) => {
    setReferrals(referrals.map(ref => 
      ref.id === id ? { ...ref, status: 'Approved' } : ref
    ));
  };

  const handleReject = (id) => {
    setReferrals(referrals.filter(ref => ref.id !== id));
  };

  const handleViewProfile = (user) => {
    setSelectedUser(user);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <DashboardCard title="Referral Requests" icon={FaUserFriends}>
        <AnimatePresence>
          <ul className="space-y-4">
            {referrals.map(referral => (
              <ReferralCard
                key={referral.id}
                referral={referral}
                onAccept={handleAccept}
                onReject={handleReject}
                onViewProfile={handleViewProfile}
              />
            ))}
          </ul>
        </AnimatePresence>
      </DashboardCard>

      <DashboardCard title="Referral Statistics" icon={FaChartBar}>
        <ReferralStatisticsChart />
      </DashboardCard>

      <AnimatePresence>
        {selectedUser && (
          <UserProfileModal
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReferralsTab;